import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UserSurvey from '../components/onboarding/UserSurvey';
import { CheckCircle, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const OnboardingFlow = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [completed, setCompleted] = useState(false);
 const [recommendations, setRecommendations] = useState(null);

 const handleSurveyComplete = async (surveyData) => {
 try {
 // Map wizard's camelCase state -> backend SurveyProfile snake_case contract.
 const payload = {
 first_name: surveyData.first_name || undefined,
 last_name: surveyData.last_name || undefined,
 phone: surveyData.phone || undefined,
 contact_email: surveyData.contact_email || undefined,
 user_type: surveyData.userType,
 business_type: surveyData.businessType || undefined,
 business_size: surveyData.businessSize || undefined,
 has_machine: surveyData.hasMachine,
 machine_type: surveyData.machineType || [],
 machine_count: Number(surveyData.machineCount) || 1,
 planned_purchase: surveyData.plannedPurchase || undefined,
 primary_interest: surveyData.primaryInterest || [],
 business_goals: surveyData.businessGoals || [],
 experience_level: surveyData.experienceLevel,
 region: surveyData.region,
 age_range: surveyData.ageRange || undefined,
 content_preference: surveyData.contentPreference || [],
 learning_style: surveyData.learningStyle,
 };
 const response = await axios.post(`${API}/user-survey`, payload);
 setRecommendations(response.data.recommendations);
 setCompleted(true);
 toast.success('Profile setup complete! Your experience is now personalized.');
 } catch (error) {
 console.error('Survey submission error:', error);
 toast.error('Failed to save survey. Please try again.');
 }
 };

 const handleFinish = () => {
 navigate('/dashboard');
 };

 if (completed && recommendations) {
 return (
 <div className="min-h-screen bg-background dark:from-slate-900 dark:to-slate-800 p-6">
 <div className="max-w-4xl mx-auto">
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
 <CheckCircle className="w-12 h-12 text-green-600" />
 </div>
 <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
 You're All Set!
 </h1>
 <p className="text-lg text-slate-600 dark:text-neutral-400">
 We've customized your experience based on your preferences
 </p>
 </div>

 <div className="grid md:grid-cols-2 gap-6 mb-8">
 {/* Recommended Courses */}
 {recommendations.courses && recommendations.courses.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-primary" />
 Recommended Courses
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {recommendations.courses.slice(0, 5).map((course, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="text-primary mt-1">✓</span>
 <span className="text-sm text-slate-700 dark:text-neutral-300">{course}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 )}

 {/* Featured Products */}
 {recommendations.products && recommendations.products.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 🛒 Product Recommendations
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {recommendations.products.slice(0, 5).map((product, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="text-orange-600 mt-1">✓</span>
 <span className="text-sm text-slate-700 dark:text-neutral-300">{product}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 )}

 {/* Key Features */}
 {recommendations.features_to_highlight && recommendations.features_to_highlight.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 ⚡ Features for You
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {recommendations.features_to_highlight.map((feature, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="text-blue-600 mt-1">✓</span>
 <span className="text-sm text-slate-700 dark:text-neutral-300">{feature}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 )}

 {/* Next Steps */}
 {recommendations.onboarding_path && recommendations.onboarding_path.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 🎯 Next Steps
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ol className="space-y-2">
 {recommendations.onboarding_path.map((step, index) => (
 <li key={index} className="flex items-start gap-3">
 <span className="flex-shrink-0 w-6 h-6 bg-primary/10 text-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
 {index + 1}
 </span>
 <span className="text-sm text-slate-700 dark:text-neutral-300">{step}</span>
 </li>
 ))}
 </ol>
 </CardContent>
 </Card>
 )}
 </div>

 <div className="text-center">
 <Button
 size="lg"
 onClick={handleFinish}
 className="bg-primary/10 text-primary px-8 py-6 text-lg"
 >
 Go to Dashboard
 </Button>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mt-4">
 You can update your preferences anytime in settings
 </p>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-background dark:from-slate-900 dark:to-slate-800 py-12">
 <div className="text-center mb-8">
 <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
 Welcome to Freeze Dry Academy! 🎓
 </h1>
 <p className="text-lg text-slate-600 dark:text-neutral-400">
 Let's personalize your experience in just a few quick questions
 </p>
 <button
 type="button"
 onClick={() => {
   try { localStorage.setItem('survey_skipped', new Date().toISOString()); } catch (e) { /* ignore */ }
   navigate('/dashboard');
 }}
 className="mt-4 text-xs font-semibold underline text-muted-foreground hover:text-foreground"
 data-testid="onboarding-skip-now"
 >
 Skip for now — I'll finish this later
 </button>
 </div>
 
 <UserSurvey onComplete={handleSurveyComplete} />
 </div>
 );
};

export default OnboardingFlow;
